import { useCustom } from "@refinedev/core";
import { Card, Typography } from "antd";
import { 
    ArrowUpOutlined, 
    ArrowDownOutlined,
    DollarOutlined,
    ShoppingCartOutlined,
    FileTextOutlined,
    InboxOutlined,
} from "@ant-design/icons";
import { useEffect } from "react";
import { useNetworkTracing } from "../../hooks/useTracing";

const { Title, Text } = Typography;

// Stat Card Component
interface StatCardProps {
    title: string;
    value: number | string;
    prefix?: string;
    suffix?: React.ReactNode;
    icon: React.ReactNode;
    iconType: 'sales' | 'purchases' | 'quotations' | 'inventory';
    trend?: 'up' | 'down';
    precision?: number;
}

const StatCard: React.FC<StatCardProps> = ({ 
    title, 
    value, 
    prefix = '', 
    suffix,
    icon, 
    iconType,
    trend,
    precision = 2
}) => {
    const formattedValue = typeof value === 'number' 
        ? value.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : value;

    return (
        <div className="stat-card">
            <div className="stat-card-header">
                <div className={`stat-card-icon ${iconType}`}>
                    {icon}
                </div>
                <span className="stat-card-title">{title}</span>
            </div>
            <div className={`stat-card-value ${trend === 'up' ? 'success' : trend === 'down' ? 'danger' : ''}`}>
                {prefix}{formattedValue}
                {trend && (
                    <span className={`stat-trend ${trend}`}>
                        {trend === 'up' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                    </span>
                )}
                {suffix}
            </div>
        </div>
    );
};

// Detail Stat Item Component
interface StatItemProps {
    label: string;
    value: number | string;
    prefix?: string;
    suffix?: string;
    colorType?: 'default' | 'success' | 'danger' | 'warning';
    precision?: number;
}

const StatItem: React.FC<StatItemProps> = ({ 
    label, 
    value, 
    prefix = '', 
    suffix = '',
    colorType = 'default',
    precision = 2
}) => {
    const formattedValue = typeof value === 'number' 
        ? value.toFixed(precision).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        : value;

    return (
        <div className="stat-item">
            <div className="stat-item-label">{label}</div>
            <div className={`stat-item-value ${colorType}`}>
                {prefix}{formattedValue}{suffix}
            </div>
        </div>
    );
};

export const Dashboard: React.FC = () => {
    const { trackDataProviderOperation } = useNetworkTracing();

    // Track RefineJS data provider calls for network observability
    const { data: salesStats, isLoading: salesLoading, isError: salesError } = useCustom({
        url: "sales/stats",
        method: "get",
        queryOptions: {
            enabled: true,
        },
    });

    const { data: purchaseStats, isLoading: purchasesLoading, isError: purchasesError } = useCustom({
        url: "purchases/stats",
        method: "get",
        queryOptions: {
            enabled: true,
        },
    });

    const { data: quotationStats, isLoading: quotationsLoading, isError: quotationsError } = useCustom({
        url: "quotations/stats",
        method: "get",
        queryOptions: {
            enabled: true,
        },
    });

    const { data: inventoryStats, isLoading: inventoryLoading, isError: inventoryError } = useCustom({
        url: "inventory/stats",
        method: "get",
        queryOptions: {
            enabled: true,
        },
    });

    // Debug logging for stats data
    useEffect(() => {
        console.log('Dashboard Stats Debug:', {
            salesStats: salesStats?.data,
            salesLoading,
            salesError,
            purchaseStats: purchaseStats?.data,
            purchasesLoading,
            purchasesError,
            quotationStats: quotationStats?.data,
            quotationsLoading,
            quotationsError,
            inventoryStats: inventoryStats?.data,
            inventoryLoading,
            inventoryError
        });
    }, [salesStats, purchaseStats, quotationStats, inventoryStats, salesLoading, purchasesLoading, quotationsLoading, inventoryLoading]);

    // Track dashboard load as a composite network operation
    useEffect(() => {
        const span = trackDataProviderOperation('dashboard', 'load_stats', {
            'dashboard.stats_count': 4,
            'dashboard.component': 'main',
        });

        const timer = setTimeout(() => {
            span.addEvent('dashboard.stats.loaded');
            span.end();
        }, 2000);

        return () => {
            clearTimeout(timer);
            span.end();
        };
    }, []);

    return (
        <div className="dashboard-page">
            <Title level={2} className="dashboard-title">Dashboard</Title>
            
            {/* Top Stats Cards */}
            <div className="stats-grid">
                <StatCard
                    title="Total Sales"
                    value={salesStats?.data?.monthly?.total || 0}
                    prefix="$ "
                    icon={<DollarOutlined />}
                    iconType="sales"
                    trend="up"
                />
                <StatCard
                    title="Total Purchases"
                    value={purchaseStats?.data?.monthly?.value || 0}
                    prefix="$ "
                    icon={<ShoppingCartOutlined />}
                    iconType="purchases"
                    trend="down"
                />
                <StatCard
                    title="Active Quotations"
                    value={quotationStats?.data?.monthly?.count || 0}
                    icon={<FileTextOutlined />}
                    iconType="quotations"
                    precision={0}
                />
                <StatCard
                    title="Low Stock Items"
                    value={inventoryStats?.data?.lowStock || 0}
                    icon={<InboxOutlined />}
                    iconType="inventory"
                    precision={0}
                />
            </div>

            {/* Detail Cards Row 1 */}
            <div className="detail-cards-grid">
                <Card title="Recent Sales" className="detail-card">
                    <StatItem
                        label="Today's Sales (Total)"
                        value={salesStats?.data?.daily?.total || 0}
                        prefix="$ "
                    />
                    {salesStats?.data?.daily?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`daily-${currencyStat.currency}`}
                            label={`Today's Sales (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.total || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                    
                    <div className="stat-divider" />
                    
                    <StatItem
                        label="This Week (Total)"
                        value={salesStats?.data?.weekly?.total || 0}
                        prefix="$ "
                    />
                    {salesStats?.data?.weekly?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`weekly-${currencyStat.currency}`}
                            label={`This Week (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.total || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                    
                    <div className="stat-divider" />
                    
                    <StatItem
                        label="This Month (Total)"
                        value={salesStats?.data?.monthly?.total || 0}
                        prefix="$ "
                    />
                    {salesStats?.data?.monthly?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`monthly-${currencyStat.currency}`}
                            label={`This Month (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.total || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                </Card>

                <Card title="Recent Purchases" className="detail-card">
                    <StatItem
                        label="Today's Purchases (Total)"
                        value={purchaseStats?.data?.daily?.value || 0}
                        prefix="$ "
                    />
                    {purchaseStats?.data?.daily?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`daily-purchase-${currencyStat.currency}`}
                            label={`Today's Purchases (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.value || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                    
                    <div className="stat-divider" />
                    
                    <StatItem
                        label="This Week (Total)"
                        value={purchaseStats?.data?.weekly?.value || 0}
                        prefix="$ "
                    />
                    {purchaseStats?.data?.weekly?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`weekly-purchase-${currencyStat.currency}`}
                            label={`This Week (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.value || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                    
                    <div className="stat-divider" />
                    
                    <StatItem
                        label="This Month (Total)"
                        value={purchaseStats?.data?.monthly?.value || 0}
                        prefix="$ "
                    />
                    {purchaseStats?.data?.monthly?.byCurrency?.map((currencyStat: any) => (
                        <StatItem
                            key={`monthly-purchase-${currencyStat.currency}`}
                            label={`This Month (${currencyStat.currency || 'Unknown'})`}
                            value={currencyStat.value || 0}
                            prefix={currencyStat.currency === 'USD' ? '$ ' : currencyStat.currency === 'EUR' ? '€ ' : ''}
                        />
                    ))}
                </Card>
            </div>

            {/* Detail Cards Row 2 */}
            <div className="detail-cards-grid">
                <Card title="Quotation Statistics" className="detail-card">
                    <StatItem
                        label="Daily Count"
                        value={quotationStats?.data?.daily?.count || 0}
                        precision={0}
                    />
                    <StatItem
                        label="Weekly Count"
                        value={quotationStats?.data?.weekly?.count || 0}
                        precision={0}
                    />
                    <StatItem
                        label="Acceptance Rate"
                        value={(quotationStats?.data?.acceptanceRate || 0) * 100}
                        suffix="%"
                        colorType="success"
                        precision={0}
                    />
                    <StatItem
                        label="Conversion Rate"
                        value={(quotationStats?.data?.conversionRate || 0) * 100}
                        suffix="%"
                        colorType={quotationStats?.data?.conversionRate > 0.5 ? 'success' : 'danger'}
                        precision={0}
                    />
                </Card>

                <Card title="Inventory Status" className="detail-card">
                    <StatItem
                        label="Total Items"
                        value={inventoryStats?.data?.totalItems || 0}
                        precision={0}
                    />
                    <StatItem
                        label="Out of Stock"
                        value={inventoryStats?.data?.outOfStock || 0}
                        colorType="danger"
                        precision={0}
                    />
                    <StatItem
                        label="Low Stock"
                        value={inventoryStats?.data?.lowStock || 0}
                        colorType="warning"
                        precision={0}
                    />
                </Card>
            </div>
        </div>
    );
};
